import { inject } from '@angular/core';
import { getMessageError } from '@mkp/shared/data-access';
import { legalEntityGuardActions } from '@mkp/legal-entity/actions';
import { accountMembershipActions } from '@mkp/account-membership/actions';
import { AccountMembershipResource } from '@mkp/account-membership/data-access';
import {
  AccountResource,
  addLegalEntityToAccountDto,
  addLegalEntityToAccountsDto,
  filterAccountsWithUserMemberships,
} from '@mkp/account/data-access';
import { accountApiActions } from '@mkp/account/data-access/actions';
import { authActions, authOnboardingRecoveryActions } from '@mkp/auth/actions';
import { forbiddenPageActions } from '@mkp/core/feature-errors/actions';
import { getOptionalContext } from '@mkp/core/feature-interceptor';
import { permissionGuardActions } from '@mkp/core/state/actions';
import { isActiveLegalEntity, LegalEntityResource } from '@mkp/legal-entity/data-access';
import { isHttpErrorResponse } from '@mkp/shared/util-format';
import { accountAggregateApiActions } from '@mkp/user-account-membership/actions';
import { AccountAggregateService } from '@mkp/user-account-membership/feature-account-aggregate';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { selectIsBoUser, selectLoggedInUserId } from '@mkp/user/state-logged-in/selectors';
import { catchError, filter, map, of, pipe, switchMap } from 'rxjs';
import { selectSelectedAccount, selectSelectedAccountId } from './account.selectors';

export const fetchAccountsAfterLogin = createEffect(
  (
    actions$ = inject(Actions),
    accountResource = inject(AccountResource),
    legalEntityResource = inject(LegalEntityResource),
    accountMembershipResource = inject(AccountMembershipResource),
    store = inject(Store)
  ) =>
    actions$.pipe(
      ofType(authActions.verifiedUserAuthenticated),
      concatLatestFrom(() => [store.select(selectIsBoUser), store.select(selectLoggedInUserId)]),
      switchMap(([, isBoUser, loggedInUserId]) =>
        accountResource.getAll(getOptionalContext()).pipe(
          map(({ _embedded: { results: accounts } }) => accounts),
          isBoUser
            ? pipe() // bo-user doesn't have any account-membership
            : filterAccountsWithUserMemberships(accountMembershipResource, loggedInUserId),
          addLegalEntityToAccountsDto(legalEntityResource),
          map((accounts) =>
            isBoUser
              ? authActions.loadAccountsAfterBOLoginSuccess({ accounts })
              : authActions.fetchedAccountsAfterLogin({ accounts })
          ),
          catchError((error: unknown) =>
            of(
              authActions.loadAccountsAfterLoginFailed({
                error: getMessageError(error, 'fetchAccountsAfterLogin'),
              })
            )
          )
        )
      )
    ),
  { functional: true }
);

export const handleNoAccountsAfterLogin = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(authActions.fetchedAccountsAfterLogin),
      map(({ accounts }) =>
        accounts.length
          ? authActions.loadAccountsAfterLoginSuccess({ accounts })
          : authActions.noAccountsFoundAfterUserLogin()
      )
    ),
  { functional: true }
);

export const reinitiateFromSuccessfulOnboardingRecovery = createEffect(
  (
    actions$ = inject(Actions),
    accountResource = inject(AccountResource),
    legalEntityResource = inject(LegalEntityResource)
  ) =>
    actions$.pipe(
      ofType(authOnboardingRecoveryActions.onboardingRecoverySuccess),
      switchMap(() =>
        accountResource.getAll().pipe(
          map(({ _embedded: { results: accounts } }) => accounts),
          addLegalEntityToAccountsDto(legalEntityResource),
          map((accounts) => authActions.loadAccountsAfterLoginSuccess({ accounts })),
          catchError((error: unknown) =>
            of(
              authActions.loadAccountsAfterLoginFailed({
                error: getMessageError(error, 'reinitiateFromSuccessfulOnboardingRecovery'),
              })
            )
          )
        )
      )
    ),
  { functional: true }
);

export const fetchUserAccounts = createEffect(
  (
    actions$ = inject(Actions),
    accountResource = inject(AccountResource),
    legalEntityResource = inject(LegalEntityResource)
  ) =>
    actions$.pipe(
      ofType(accountApiActions.loadUserAccounts),
      switchMap(() =>
        accountResource.getAll().pipe(
          map(({ _embedded: { results: accounts } }) => accounts),
          addLegalEntityToAccountsDto(legalEntityResource),
          map((accounts) => accountApiActions.loadUserAccountsSuccess({ accounts })),
          catchError((error: unknown) =>
            of(
              accountApiActions.loadUserAccountsFailure({
                error: getMessageError(error, 'fetchUserAccounts'),
              })
            )
          )
        )
      )
    ),
  { functional: true }
);

const refreshSelectedAccount = (
  accountId: string,
  accountResource: AccountResource,
  legalEntityResource: LegalEntityResource
) =>
  accountResource.getById(accountId).pipe(
    addLegalEntityToAccountDto(legalEntityResource),
    map((account) => accountApiActions.refreshSelectedAccountSuccess({ account })),
    catchError((error: unknown) => {
      const is403ErrorStatus = isHttpErrorResponse(error) && error.status === 403;
      const action = is403ErrorStatus
        ? accountApiActions.refreshSelectedAccountNotFound({ accountId })
        : accountApiActions.refreshSelectedAccountFailure({
            error: getMessageError(error, 'refreshSelectedAccount'),
          });
      return of(action);
    })
  );

export const refreshAccountAggregateOnSelectedAccountNotFound = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    accountAggregateService = inject(AccountAggregateService)
  ) => {
    return actions$.pipe(
      ofType(accountApiActions.refreshSelectedAccountNotFound),
      concatLatestFrom(() => store.select(selectLoggedInUserId)),
      map(([, loggedInUserId]) => loggedInUserId),
      filter(Boolean),
      switchMap((loggedInUserId) =>
        accountAggregateService.refreshAccountAggregate(loggedInUserId).pipe(
          map(accountAggregateApiActions.refreshOnPermissionCheckSuccess),
          catchError((error: unknown) =>
            of(accountAggregateApiActions.refreshOnPermissionCheckFailure({ error }))
          )
        )
      )
    );
  },
  { functional: true }
);

// when a user change their own permissions: we want to update the menu by refreshing the selected account
export const refreshSelectedAccountOnSelfRolesUpdate = createEffect(
  (
    actions$ = inject(Actions),
    accountResource = inject(AccountResource),
    legalEntityResource = inject(LegalEntityResource)
  ) =>
    actions$.pipe(
      ofType(accountMembershipActions.patchRolesSuccess),
      filter(({ isSelfUpdate }) => isSelfUpdate),
      switchMap(({ accountMembership }) =>
        refreshSelectedAccount(accountMembership.accountId, accountResource, legalEntityResource)
      )
    ),
  { functional: true }
);

// when a user is redirected to forbidden page: we want to update the menu by refreshing the selected account
export const refreshSelectedAccountOnForbiddenPage = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    accountResource = inject(AccountResource),
    legalEntityResource = inject(LegalEntityResource)
  ) =>
    actions$.pipe(
      ofType(forbiddenPageActions.opened, permissionGuardActions.canActivate),
      concatLatestFrom(() => store.select(selectSelectedAccountId).pipe(filter(Boolean))),
      switchMap(([, accountId]) =>
        refreshSelectedAccount(accountId, accountResource, legalEntityResource)
      )
    ),
  { functional: true }
);

// when a user deletes their own permissions: we need to refresh all the accounts
export const refreshAllAccountsOnSelfRolesDelete = createEffect(
  (
    actions$ = inject(Actions),
    accountResource = inject(AccountResource),
    legalEntityResource = inject(LegalEntityResource)
  ) =>
    actions$.pipe(
      ofType(accountMembershipActions.deleteSuccess, accountMembershipActions.deleteFailure),
      filter(({ isSelfDelete }) => isSelfDelete),
      filter(isSuccessOr404Failure),
      switchMap(() =>
        accountResource.getAll().pipe(
          map(({ _embedded: { results: accounts } }) => accounts),
          addLegalEntityToAccountsDto(legalEntityResource),
          map((accounts) => accountApiActions.refreshAllAccountsSuccess({ accounts })),
          catchError((error: unknown) =>
            of(
              accountApiActions.refreshAllAccountsFailure({
                error: getMessageError(error, 'refreshAllAccountsOnSelfRolesDelete'),
              })
            )
          )
        )
      )
    ),
  { functional: true }
);

// when legal-entity is inactive, we want to regularly check if legal-entity becomes active
// this is due to the fact that customer service usually activates an account within 5min
// we want reactive UI when this happens
export const checkActiveLegalEntity = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    legalEntityResource = inject(LegalEntityResource),
    accountResource = inject(AccountResource)
  ) =>
    actions$.pipe(
      // this guard is not blocking users, its only purpose is to trigger this refresh
      ofType(legalEntityGuardActions.canActivate),
      concatLatestFrom(() => store.select(selectSelectedAccount)),
      map(([, account]) => account),
      filter(Boolean),
      filter((account) => !isActiveLegalEntity(account.legalEntity)),
      switchMap(({ id: accountId }) =>
        accountResource.getById(accountId).pipe(
          addLegalEntityToAccountDto(legalEntityResource),
          map((account) => accountApiActions.loadAccountSuccess({ account })),
          catchError((error: unknown) =>
            of(
              accountApiActions.loadAccountFailure({
                error: getMessageError(error, 'checkActiveLegalEntity'),
              })
            )
          )
        )
      )
    ),
  { functional: true }
);

type DeleteSuccessOrFailure = ReturnType<
  typeof accountMembershipActions.deleteSuccess | typeof accountMembershipActions.deleteFailure
>;
const isSuccessOr404Failure = (action: DeleteSuccessOrFailure): boolean =>
  action.type === accountMembershipActions.deleteSuccess.type || action.status === 404;
